import { useEffect } from 'react';

/**
 * Publishes the *visible* viewport height as `--z2-app-height` on <html>.
 *
 * Why this exists on top of `100dvh`:
 *   - `100vh` does not shrink when a mobile keyboard opens, so a bottom-pinned
 *     composer ends up behind the keyboard.
 *   - `100dvh` tracks the collapsing browser toolbar, but iOS Safari does NOT
 *     resize the layout viewport for the keyboard either.
 *   - `window.visualViewport` reports exactly what the user can see, keyboard
 *     included, on both platforms.
 *
 * The app shell is `height: var(--z2-app-height)` (see index.css), so when the
 * keyboard opens the whole shell gets shorter, the message list (the only
 * scrolling pane) flexes, and the composer stays on screen. Without JS support
 * the shell falls back to `100dvh`/`100vh`, which is still correct on desktop.
 */
export function useViewportHeight() {
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;

    const apply = () => {
      const height = viewport ? viewport.height : window.innerHeight;
      if (!height) return;
      // Rounded to whole pixels so the value does not thrash React layouts
      // while the toolbar animates.
      root.style.setProperty('--z2-app-height', `${Math.round(height)}px`);
    };

    apply();

    // `resize` covers the keyboard and the collapsing toolbar; `scroll` covers
    // the visual viewport panning on iOS, which also changes what is visible.
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    viewport?.addEventListener('resize', apply);
    viewport?.addEventListener('scroll', apply);

    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      viewport?.removeEventListener('resize', apply);
      viewport?.removeEventListener('scroll', apply);
      // Fall back to the stylesheet value rather than leaving a stale px height.
      root.style.removeProperty('--z2-app-height');
    };
  }, []);
}
